import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Fade,
  Grow,
  Zoom,
  useTheme,
  useMediaQuery,
  CardHeader,
  Avatar,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/CloudUpload';
import BulkQueryIcon from '@mui/icons-material/DynamicFeed';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BiotechIcon from '@mui/icons-material/Biotech';
import SchoolIcon from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedIcon from '@mui/icons-material/Verified';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useAppContext } from '../context/AppContext';
import { healthCheck } from '../services/api';
import { useTranslation } from 'react-i18next';

// Feature card component
const FeatureCard = ({ icon, title, description, delay = 0 }) => {
  return (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000 + delay}>
      <Card
        elevation={2}
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 4,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 8,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.light',
              width: 56,
              height: 56,
              mr: 2,
              boxShadow: 2,
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="h6" component="h3" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Card>
    </Grow>
  );
};

// Action card component
const ActionCard = ({ title, description, icon, buttonText, buttonIcon, to, color = "primary", delay = 0 }) => {
  const theme = useTheme();

  return (
    <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 8,
          },
        }}
      >
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: `${color}.main` }}>
              {icon}
            </Avatar>
          }
          title={<Typography variant="h6" fontWeight="bold">{title}</Typography>}
          sx={{
            bgcolor: `${color}.light`,
            color: theme.palette.getContrastText(theme.palette[color].light),
            pb: 1,
          }}
        />
        <CardContent sx={{ flexGrow: 1, pt: 2 }}>
          <Typography variant="body1" paragraph>
            {description}
          </Typography>
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            component={RouterLink}
            to={to}
            variant="contained"
            color={color}
            endIcon={buttonIcon}
            fullWidth
            size="large"
            sx={{
              borderRadius: 8,
              py: 1,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {buttonText}
          </Button>
        </CardActions>
      </Card>
    </Zoom>
  );
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { topics, loading } = useAppContext();
  const { t } = useTranslation();
  const [apiStatus, setApiStatus] = useState({ status: 'checking', message: null });
  const [animationReady, setAnimationReady] = useState(false);

  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        const response = await healthCheck();
        if (response.status === 'ok') {
          setApiStatus({ status: 'online', message: response.message });
        } else {
          setApiStatus({ status: 'warning', message: 'API is responding but may have issues.' });
        }
      } catch (error) {
        setApiStatus({ status: 'offline', message: 'API is not responding. Please check the server.' });
      }
    };

    checkApiHealth();

    // Trigger animations after a short delay
    const timer = setTimeout(() => {
      setAnimationReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Fade in={animationReady} timeout={1000}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 8 },
            mb: 6,
            textAlign: 'center',
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -80,
              left: -80,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              zIndex: 0,
            }}
          />

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                mb: 2,
              }}
            >
              {t('home.welcome')}
            </Typography>

            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              gutterBottom
              sx={{
                mb: 4,
                maxWidth: 800,
                mx: 'auto',
                opacity: 0.9,
              }}
            >
              {t('home.subtitle')}
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Button
                component={RouterLink}
                to="/orchestration"
                variant="contained"
                color="secondary"
                size="large"
                endIcon={<SearchIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 8,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t('home.startMultiSourceSearch')}
              </Button>

              <Button
                component={RouterLink}
                to="/documents"
                variant="outlined"
                size="large"
                endIcon={<UploadIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 8,
                  borderColor: 'white',
                  color: 'white',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-3px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Upload Documents
              </Button>
            </Stack>

            {apiStatus.status === 'checking' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                <Typography>{t('home.apiStatus.checking')}</Typography>
              </Box>
            ) : apiStatus.status === 'online' ? (
              <Chip
                icon={<VerifiedIcon />}
                label={`${t('home.apiStatus.online')}: ${apiStatus.message}`}
                color="success"
                variant="filled"
                sx={{
                  px: 2,
                  py: 2.5,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  },
                }}
              />
            ) : apiStatus.status === 'warning' ? (
              <Chip
                label={t('home.apiStatus.warning')}
                color="warning"
                variant="filled"
                sx={{
                  px: 2,
                  py: 2.5,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  },
                }}
              />
            ) : (
              <Chip
                label={t('home.apiStatus.offline')}
                color="error"
                variant="filled"
                sx={{
                  px: 2,
                  py: 2.5,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  },
                }}
              />
            )}
          </Box>
        </Paper>
      </Fade>

      {/* Main Actions Section */}
      <Typography
        variant="h4"
        component="h2"
        align="center"
        gutterBottom
        fontWeight="bold"
        sx={{ mb: 1 }}
      >
        {t('home.getStarted')}
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}
      >
        {t('home.getStartedSubtitle')}
      </Typography>

      <Grid container spacing={4} sx={{ mb: 8 }}>
        <Grid item xs={12} md={3}>
          <ActionCard
            title={t('home.features.multiSource.title')}
            description={t('home.features.multiSource.description')}
            icon={<SearchIcon />}
            buttonText={t('home.features.multiSource.button')}
            buttonIcon={<ArrowForwardIcon />}
            to="/orchestration"
            color="primary"
            delay={100}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <ActionCard
            title={t('home.features.interactiveReasoning.title')}
            description={t('home.features.interactiveReasoning.description')}
            icon={<TimelineIcon />}
            buttonText={t('home.features.interactiveReasoning.button')}
            buttonIcon={<ArrowForwardIcon />}
            to="/interactive-reasoning"
            color="secondary"
            delay={150}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <ActionCard
            title={t('home.features.simpleQuery.title')}
            description={t('home.features.simpleQuery.description')}
            icon={<PsychologyIcon />}
            buttonText={t('home.features.simpleQuery.button')}
            buttonIcon={<ArrowForwardIcon />}
            to="/query"
            color="success"
            delay={200}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <ActionCard
            title={t('home.features.documentManagement.title')}
            description={t('home.features.documentManagement.description', { count: loading ? t('common.loading') : topics.length })}
            icon={<UploadIcon />}
            buttonText={t('home.features.documentManagement.button')}
            buttonIcon={<ArrowForwardIcon />}
            to="/documents"
            color="warning"
            delay={250}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <ActionCard
            title={t('home.features.bulkProcessing.title')}
            description={t('home.features.bulkProcessing.description')}
            icon={<BulkQueryIcon />}
            buttonText={t('home.features.bulkProcessing.button')}
            buttonIcon={<ArrowForwardIcon />}
            to="/bulk-query"
            color="info"
            delay={300}
          />
        </Grid>
      </Grid>

      {/* Features Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          align="center"
          gutterBottom
          fontWeight="bold"
          sx={{ mb: 1 }}
        >
          {t('home.keyFeatures')}
        </Typography>

        <Typography
          variant="subtitle1"
          align="center"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}
        >
          {t('home.keyFeaturesSubtitle')}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<PsychologyIcon fontSize="large" />}
              title={t('home.features.rag.title')}
              description={t('home.features.rag.description')}
              delay={100}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SchoolIcon fontSize="large" />}
              title={t('home.features.domainSpecific.title')}
              description={t('home.features.domainSpecific.description')}
              delay={200}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<HealthAndSafetyIcon fontSize="large" />}
              title={t('home.features.sourceCitations.title')}
              description={t('home.features.sourceCitations.description')}
              delay={300}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SpeedIcon fontSize="large" />}
              title={t('home.features.fastEfficient.title')}
              description={t('home.features.fastEfficient.description')}
              delay={400}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<BiotechIcon fontSize="large" />}
              title={t('home.features.customizable.title')}
              description={t('home.features.customizable.description')}
              delay={500}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SettingsIcon fontSize="large" />}
              title={t('home.features.transparent.title')}
              description={t('home.features.transparent.description')}
              delay={600}
            />
          </Grid>
        </Grid>
      </Box>

      {/* About Section */}
      <Fade in={animationReady} timeout={1500}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
          }}
        >
          <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
            {t('home.aboutTitle')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" paragraph>
            {t('home.aboutDescription1')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('home.aboutDescription2')}
          </Typography>
          <Typography variant="body1">
            {t('home.aboutDescription3')}
          </Typography>
        </Paper>
      </Fade>
    </Box>
  );
};

export default HomePage;
